import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { HTTP_RESPONSE_STATUS } from 'common/constants/httpMethodsConstants'
import type { IAlert } from 'common/interfaces/IAlert'

import type { SeriesApiError, SeriesSaveWarning } from 'features/Series/api/series.types'
import { SERIES_TEXT } from 'features/Series/series.constants'

export const SERIES_BOOKING_TEXT = {
  title: 'Series',
  booked: 'The Series is booked. Its visits appear on the calendar as OpenEMR sends them.',
  bookedWithWarnings: 'The Series is booked, with these notes:',
} as const

const FORBIDDEN = 403
const INVALID_INPUT = 'invalid_input'
const EMR_UNAVAILABLE = 'emr_unavailable'

const warningLine = ({ step, message }: SeriesSaveWarning): string =>
  step === null ? message : `Visit ${step}: ${message}`

export const seriesBookedNotice = (warnings: SeriesSaveWarning[]): IAlert =>
  warnings.length
    ? {
        title: SERIES_BOOKING_TEXT.title,
        type: ALERT_CONSTANTS.WARNING,
        description: [SERIES_BOOKING_TEXT.bookedWithWarnings, ...warnings.map(warningLine)],
      }
    : {
        title: SERIES_BOOKING_TEXT.title,
        type: ALERT_CONSTANTS.SUCCESS,
        description: SERIES_BOOKING_TEXT.booked,
      }

const failureDescription = (error: SeriesApiError): string | string[] => {
  // A 403 or 404 without a code still carries the backend's own words.
  if (error.status === FORBIDDEN || error.status === HTTP_RESPONSE_STATUS.NOT_FOUND) {
    return error.message
  }
  if (error.code === EMR_UNAVAILABLE) return SERIES_TEXT.emrUnavailableBook
  if (error.code === INVALID_INPUT) {
    const fieldErrors = Object.values(error.errors).flat()
    return [error.message, ...fieldErrors.filter((line) => line !== error.message)]
  }
  return error.message
}

export const seriesFailedNotice = (error: SeriesApiError): IAlert => ({
  title: SERIES_BOOKING_TEXT.title,
  type: ALERT_CONSTANTS.ERROR,
  description: failureDescription(error),
})

// A preview never books, so "did not answer" carries no calendar advice.
export const seriesPreviewFailedNotice = (error: SeriesApiError): IAlert =>
  error.code === EMR_UNAVAILABLE
    ? {
        title: SERIES_BOOKING_TEXT.title,
        type: ALERT_CONSTANTS.ERROR,
        description: SERIES_TEXT.requestFailed,
      }
    : seriesFailedNotice(error)
